/**
 * The bulk editor's target list — building and filtering it (OPEN-053).
 *
 * Pure, so the filtering rules are tested directly rather than through a modal with drag
 * handlers and a context menu attached.
 *
 * Sites and wells are deliberately ONE list rather than two tabs. The dispatcher's mental
 * model is "everywhere this person calls loads from", and that spans both — splitting them
 * means selecting a customer's sites, assigning, then remembering to repeat it for their
 * standalone wells, which is precisely the step that gets skipped.
 */

import type { BulkTarget } from './slot-edit.js';

export interface BulkRow extends BulkTarget {
  /** The customer this location belongs to, for the customer filter. Null when unknown. */
  readonly customer: string | null;
  /** Secondary identifier shown under the name (API number, operator abbreviation). */
  readonly sub: string | null;
}

interface BatteryLike {
  readonly id: string;
  readonly name: string;
  readonly isActive: boolean;
  readonly operatorName: string;
  readonly operatorAbbreviation: string;
}

interface WellLike {
  readonly id: string;
  readonly wellName: string;
  readonly apiNumber: string;
  readonly operatorName: string | null;
  readonly inMyCatalog?: boolean;
}

/**
 * One list of everything assignable.
 *
 * Inactive sites are dropped: assigning a requestor to a site nobody dispatches to is noise,
 * and including them inflates the "12 locations" count in the confirm with locations the
 * dispatcher cannot see on the Sites tab either.
 */
export function buildBulkRows(
  batteries: readonly BatteryLike[],
  wells: readonly WellLike[],
): BulkRow[] {
  const rows: BulkRow[] = [];
  for (const b of batteries) {
    if (!b.isActive) continue;
    rows.push({
      targetType: 'TANK_BATTERY',
      targetId: b.id,
      label: b.name,
      customer: b.operatorName,
      sub: b.operatorAbbreviation,
    });
  }
  for (const w of wells) {
    rows.push({
      targetType: 'WELL',
      targetId: w.id,
      label: w.wellName,
      customer: w.operatorName,
      sub: `API ${w.apiNumber}`,
    });
  }
  return rows;
}

export type TargetKind = 'ALL' | 'TANK_BATTERY' | 'WELL';

export interface TargetFilter {
  readonly needle: string;
  readonly kind: TargetKind;
  /** Exact customer name, or null for every customer. */
  readonly customer: string | null;
}

export function filterBulkRows(rows: readonly BulkRow[], filter: TargetFilter): BulkRow[] {
  const needle = filter.needle.trim().toLowerCase();
  return rows.filter((r) => {
    if (filter.kind !== 'ALL' && r.targetType !== filter.kind) return false;
    if (filter.customer !== null && r.customer !== filter.customer) return false;
    if (needle === '') return true;
    return (
      r.label.toLowerCase().includes(needle) ||
      (r.customer ?? '').toLowerCase().includes(needle) ||
      (r.sub ?? '').toLowerCase().includes(needle)
    );
  });
}

/** The customer names present, sorted — the options for the customer filter. */
export function customersOf(rows: readonly BulkRow[]): string[] {
  const set = new Set<string>();
  for (const r of rows) if (r.customer !== null && r.customer !== '') set.add(r.customer);
  return [...set].sort((a, b) => a.localeCompare(b));
}

/** A stable key for selection state — the target identity, since ids are unique per type. */
export function rowKey(t: BulkTarget): string {
  return `${t.targetType}:${t.targetId}`;
}
